/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import "./DonationInfoRecurring.css";
import { simpleFetch } from "../../restWrapper";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { NumericFormat } from "react-number-format";
import { Helmet } from "react-helmet";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { resolveThemeColors } from "../../assets/helper";

class DonationInfoRecurring extends Component {
  constructor(props) {
    super(props);
    if (!this.props.location.state || !this.props.location.state.paymentId) {
      this.props.history.push("/DonationRecordRecurring");
      this.state = {};
      return;
    }
    this.state = {
      ...this.props.location.state,
      paymentId: this.props.location.state.paymentId, // Store paymentId explicitly
      isEditing: false,
      draft: null,
      originalData: null,
      showCardModal: false,
      updatingCard: false,
      feeBreakdown: null,
      isFeeLoading: false
    };

    this.onClickEdit = this.onClickEdit.bind(this);
    this.onClickSave = this.onClickSave.bind(this);
    this.onClickCancel = this.onClickCancel.bind(this);
    this.onClickCancelSchedule = this.onClickCancelSchedule.bind(this);
    this.handleDraftChange = this.handleDraftChange.bind(this);
    this.handleDraftChanges = this.handleDraftChanges.bind(this);
    this.handleCoverFeeToggle = this.handleCoverFeeToggle.bind(this);
    this.fetchFeeBreakdown = this.fetchFeeBreakdown.bind(this);
    this.openCardModal = this.openCardModal.bind(this);
    this.closeCardModal = this.closeCardModal.bind(this);
    this.handleUpdateCard = this.handleUpdateCard.bind(this);
  }

  componentDidMount() {
    simpleFetch(
      "/donors/payments/" + this.state.paymentId,
      "GET",
      {},
    ).then((res) => {
      if (res.status !== 200)
        this.props.history.push("/donationRecordRecurring");
      // console.log("Payment data received:", res.body);
      // console.log("isRecurring:", res.body.isRecurring);
      // console.log("recurringInterval:", res.body.recurringInterval);
      // console.log("recurringEndDate:", res.body.recurringEndDate);

      const payment = res.body;
      this.setState({
        data: payment,
        paymentHistory: [],
        loadingHistory: false
      });

      // Fetch payment history if this is a recurring donation
      if (payment.isRecurring) {
        this.fetchPaymentHistory(payment);
      }
    });
  }

  fetchPaymentHistory(payment) {
    this.setState({ loadingHistory: true });

    // Find the root payment ID (either this payment's parent, or this payment itself)
    const rootPaymentId = payment.parentPaymentId || payment.id;

    // Fetch all completed payments in this recurring chain
    simpleFetch("/donors/payments", "GET", {
      organizationViewId: payment.organizationViewId,
      status: "complete"
    }).then((res) => {
      if (res.status === 200) {
        // Filter to only payments in this recurring chain
        const relatedPayments = res.body.filter(p =>
          (p.id === rootPaymentId || p.parentPaymentId === rootPaymentId) &&
          p.status === 'complete'
        );

        // Sort by scheduled date (oldest first)
        relatedPayments.sort((a, b) =>
          new Date(a.scheduledDate) - new Date(b.scheduledDate)
        );

        // console.log("Found", relatedPayments.length, "completed payments in recurring chain");

        this.setState({
          paymentHistory: relatedPayments,
          loadingHistory: false
        });
      } else {
        this.setState({ loadingHistory: false });
      }
    }).catch((err) => {
      // console.error("Error fetching payment history:", err);
      this.setState({ loadingHistory: false });
    });
  }

  calculateOccurrencesFromEndDate(endDate, startDate, interval) {
    // Calculate how many occurrences based on end date and interval
    if (!endDate || !startDate || !interval) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end - start;

    switch (interval) {
      case 'daily':
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      case 'weekly':
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7)) + 1;
      case 'biweekly':
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 14)) + 1;
      case 'monthly':
        return Math.ceil((end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth())) + 1;
      case 'quarterly':
        return Math.ceil((end.getFullYear() - start.getFullYear()) * 4 + Math.ceil((end.getMonth() - start.getMonth()) / 3)) + 1;
      case 'yearly':
        return (end.getFullYear() - start.getFullYear()) + 1;
      default:
        return 0;
    }
  }

  calculateEndDateFromOccurrences(occurrences, startDate, interval) {
    // Calculate end date based on occurrences and interval
    // Using (occurrences - 1) because first payment is at startDate
    if (!occurrences || occurrences <= 0 || !startDate || !interval) return null;

    const endDate = new Date(startDate);
    const periods = occurrences - 1; // First payment at start, so subtract 1

    switch (interval) {
      case 'daily':
        endDate.setDate(endDate.getDate() + periods);
        break;
      case 'weekly':
        endDate.setDate(endDate.getDate() + (periods * 7));
        break;
      case 'biweekly':
        endDate.setDate(endDate.getDate() + (periods * 14));
        break;
      case 'monthly':
        endDate.setMonth(endDate.getMonth() + periods);
        break;
      case 'quarterly':
        endDate.setMonth(endDate.getMonth() + (periods * 3));
        break;
      case 'yearly':
        endDate.setFullYear(endDate.getFullYear() + periods);
        break;
      default:
        return null;
    }

    return endDate.toISOString();
  }

  onClickEdit() {
    // Instead of navigating to edit page, enable inline editing
    const data = this.state.data;

    // Calculate current occurrences from end date
    const currentOccurrences = data.recurringEndDate
      ? this.calculateOccurrencesFromEndDate(data.recurringEndDate, data.scheduledDate, data.recurringInterval)
      : 0;

    this.setState({
      isEditing: true,
      draft: {},
      originalData: {
        amount: data.amount,
        scheduledDate: data.scheduledDate,
        memo: data.memo || '',
        isInfiniteRecurring: data.isRecurring && !data.recurringEndDate,
        recurringInterval: data.recurringInterval || 'monthly',
        recurringDay: data.recurringDay || null,
        recurringEndDate: data.recurringEndDate || null,
        recurringOccurrences: currentOccurrences || 0,
        coverFee: data.coverFee || false
      }
    }, () => {
      // If coverFee is already enabled, fetch fee breakdown for display
      if (data.coverFee) {
        const baseAmount = data.baseAmount || data.net || data.amount;
        this.fetchFeeBreakdown(baseAmount, true);
      }
    });
  }

  onClickCancel() {
    this.setState({
      isEditing: false,
      draft: null,
      feeBreakdown: null
    });
  }

  onClickCancelSchedule() {
    const languageObj = this.props.languageObj;
    const confirmMessage = languageObj.confirmCancelSchedule ||
      "Are you sure you want to cancel this recurring donation schedule? This cannot be undone.";

    if (!window.confirm(confirmMessage)) {
      return;
    }

    const paymentId = this.state.paymentId;

    simpleFetch(
      "/donors/payments/" + paymentId,
      "DELETE",
      {}
    ).then((res) => {
      if (res.status === 204) {
        alert(languageObj.scheduleCancelled || "Recurring donation schedule has been cancelled.");
        this.props.history.push("/DonationRecordRecurring");
      } else {
        alert(res.body.message || "Failed to cancel schedule. Please try again.");
      }
    }).catch((err) => {
      // console.error("Error cancelling schedule:", err);
      alert("An error occurred while cancelling the schedule.");
    });
  }

  onClickSave() {
    const paymentId = this.state.paymentId;
    const draft = this.state.draft;

    let obj = {
      paymentId: paymentId
    };

    // Only send fields that were actually modified (exist in draft)
    if (draft.hasOwnProperty('amount')) {
      obj.amount = parseFloat(draft.amount);
    }

    if (draft.hasOwnProperty('memo')) {
      obj.memo = draft.memo;
    }

    if (draft.hasOwnProperty('scheduledDate')) {
      obj.scheduledDate = draft.scheduledDate;
    }

    // Only send recurring fields if they were modified
    if (draft.hasOwnProperty('isInfiniteRecurring')) {
      if (draft.isInfiniteRecurring) {
        obj.recurringEndDate = null; // Infinite recurring
      } else if (draft.hasOwnProperty('recurringEndDate')) {
        obj.recurringEndDate = draft.recurringEndDate;
      }
    }

    if (draft.hasOwnProperty('recurringInterval')) {
      obj.recurringInterval = draft.recurringInterval;
    }

    if (draft.hasOwnProperty('recurringDay')) {
      obj.recurringDay = draft.recurringDay;
    }

    if (draft.hasOwnProperty('coverFee')) {
      obj.coverFee = draft.coverFee;
    }

    // console.log('Sending payment update (only changed fields):', obj);
    simpleFetch("/donors/payments/" + paymentId, "PUT", obj).then((res) => {
      if (res.status === 200) {
        // Refresh the payment data
        simpleFetch("/donors/payments/" + paymentId, "GET", {}).then((res) => {
          if (res.status === 200) {
            this.setState({
              data: res.body,
              isEditing: false,
              draft: null
            });
          }
        });
      } else {
        // console.error('API Error:', res);
        alert((res.body && (res.body.message || res.body.error)) || 'Failed to save changes. Check console for details.');
      }
    }).catch((error) => {
      // console.error('Request failed:', error);
      alert('Failed to save changes. Please try again.');
    });
  }

  handleDraftChange(field, value) {
    this.setState({
      draft: {
        ...this.state.draft,
        [field]: value
      }
    });
  }

  handleDraftChanges(updates) {
    // Update multiple draft fields at once to avoid race conditions
    this.setState({
      draft: {
        ...this.state.draft,
        ...updates
      }
    });
  }

  handleCoverFeeToggle(checked) {
    const data = this.state.data;
    // Fallback chain: baseAmount (new) -> net (existing payments) -> amount (last resort)
    const storedBaseAmount = data.baseAmount || data.net || data.amount;

    // Get current base amount from draft or stored value
    const currentBaseAmount = this.state.draft.hasOwnProperty('amount')
      ? parseFloat(this.state.draft.amount)
      : storedBaseAmount;

    if (!checked) {
      // Disabling cover fees: keep base amount, clear fee breakdown
      this.setState({
        draft: {
          ...this.state.draft,
          coverFee: false,
          amount: currentBaseAmount
        },
        feeBreakdown: null
      });
    } else {
      // Enabling cover fees: fetch fee breakdown for display only
      // IMPORTANT: Amount stays as base amount - scheduler adds fees at payment time
      this.setState({
        draft: {
          ...this.state.draft,
          coverFee: true,
          amount: currentBaseAmount  // Keep base amount, NOT total
        }
      });

      // Fetch fee breakdown for display purposes only
      this.fetchFeeBreakdown(currentBaseAmount, true);
    }
  }

  async fetchFeeBreakdown(amount, coverFee) {
    const data = this.state.data;

    if (!amount || amount <= 0) {
      return;
    }

    this.setState({ isFeeLoading: true });

    const feeData = {
      amount: parseFloat(amount),
      organizationId: data.organizationViewId || data.organizationId,
      coverFee: coverFee,
      paymentMethod: 'credit_card',
      cardBrand: data.cardBrand,
      cardCountry: data.cardCountry || 'HK'
    };

    try {
      const res = await simpleFetch("/user/payments/calculate-fees", "POST", feeData);

      if (res.status === 200 && res.body && typeof res.body.totalAmount === 'number') {
        // Only store feeBreakdown for display purposes
        // DO NOT update draft.amount - scheduler expects BASE amount and adds fees when coverFee=true
        this.setState({
          feeBreakdown: res.body,
          isFeeLoading: false
        });
      } else {
        // console.error('Fee breakdown unavailable:', res.body);
        this.setState({ isFeeLoading: false });
      }
    } catch (error) {
      // console.error('Error fetching fee breakdown:', error);
      this.setState({ isFeeLoading: false });
    }
  }

  openCardModal(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // console.log('Opening card modal, stripe:', this.props.stripe);
    // console.log('Opening card modal, elements:', this.props.elements);
    this.setState({ showCardModal: true });
  }

  closeCardModal() {
    this.setState({ showCardModal: false });
  }

  async handleUpdateCard() {
    if (!this.props.stripe) {
      alert('Stripe is not loaded. Please refresh the page.');
      return;
    }

    this.setState({ updatingCard: true });

    try {
      // Create payment method using Stripe
      const { paymentMethod, error } = await this.props.stripe.createPaymentMethod({
        type: 'card',
        card: this.props.elements.getElement(CardElement)
      });

      if (error) {
        alert(error.message);
        this.setState({ updatingCard: false });
        return;
      }

      // Update payment with new payment method
      const paymentId = this.state.paymentId;

      const res = await simpleFetch("/donors/payments/" + paymentId, "PUT", {
        paymentId: paymentId,
        stripePaymentMethodId: paymentMethod.id
      });

      if (res.status === 200) {
        // Refresh payment data
        const refreshRes = await simpleFetch("/donors/payments/" + paymentId, "GET", {});
        if (refreshRes.status === 200) {
          this.setState({
            data: refreshRes.body,
            showCardModal: false,
            updatingCard: false
          });
          alert('Payment method updated successfully!');
        }
      } else {
        alert(res.body.message || 'Failed to update payment method');
        this.setState({ updatingCard: false });
      }
    } catch (err) {
      // console.error('Error updating card:', err);
      alert('An error occurred while updating the payment method');
      this.setState({ updatingCard: false });
    }
  }

  renderRecord(data) {
    const rDate = new Date(data.scheduledDate);

    const d = rDate.getDate();
    const m = rDate.getMonth() + 1;
    const y = rDate.getFullYear();
    const date = y + "/" + m + "/" + d;

    const resolved = resolveThemeColors(this.state.headerColor, this.state.buttonColor);
    const color = {
      headerColor: resolved.headerColor,
      buttonColor: resolved.buttonColor,
    };

    const headerStyle = {
      background: "#" + this.state.headerColor,
    };

    const languageObj = this.props.languageObj;

    return (
      <div className="DonationInfoRecurring-view">
        <Helmet>
          <title>{this.state.orgName}</title>
        </Helmet>
        <div className="DonationInfoRecurring-bg" style={headerStyle} />
        <div className="DonationInfoRecurring-container">
          <div className="DonationInfoRecurring-header">
            <Link
              className="DonationInfoRecurring-backContainer"
              to={{
                pathname: "/DonationRecordRecurring",
                state: {
                  color: color,
                },
              }}
            >
              <div className="DonationInfoRecurring-back">
                <div className="DonationInfoRecurring-backBtn"></div>
                <div className="DonationInfoRecurring-backText">
                  {languageObj.backBtn}
                </div>
              </div>
            </Link>

            {!this.state.isEditing ? (
              <div
                className="DonationInfoRecurring-editContainer"
                onClick={this.onClickEdit}
              >
                <div className="DonationInfoRecurring-edit">
                  <span role="img" aria-label="edit">✏️</span> {languageObj.editBtn || "Edit"}
                </div>
              </div>
            ) : (
              <div className="DonationInfoRecurring-editButtons">
                {data.status === 'scheduled' && (
                  <div
                    className="DonationInfoRecurring-cancelScheduleBtn"
                    onClick={this.onClickCancelSchedule}
                    style={{
                      padding: '10px 16px',
                      background: '#FEF2F2',
                      border: '2px solid #EF4444',
                      borderRadius: '8px',
                      color: '#EF4444',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <span role="img" aria-label="cancel schedule">🚫</span> {languageObj.cancelSchedule || "Cancel Schedule"}
                  </div>
                )}
                <div
                  className="DonationInfoRecurring-cancelBtn"
                  onClick={this.onClickCancel}
                >
                  <span role="img" aria-label="close">✖️</span> {languageObj.closeBtn || "Close"}
                </div>
                <div
                  className="DonationInfoRecurring-saveBtn"
                  onClick={this.onClickSave}
                >
                  <span role="img" aria-label="save">💾</span> {languageObj.saveBtn || "Save"}
                </div>
              </div>
            )}
          </div>

          <div className="DonationInfoRecurring-main">
            <div className="DonationInfoRecurring-inputContainer">
              <div className="DonationInfoRecurring-heading">
                {this.state.orgName}
              </div>

              {/* Display recurring information */}
              {data.isRecurring && (
                <>
                  {/* Header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    marginBottom: '16px',
                    marginTop: '20px'
                  }}>
                  </div>

                  {/* Summary Card */}
                  <div style={{
                    borderRadius: '16px',
                    padding: '24px',
                    background: 'white'
                  }}>
                    {/* Type and Info banner */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '12px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        {/* Transaction Type Badge */}
                        <div style={{
                          padding: '6px 12px',
                          borderRadius: '8px',
                          background: data.type === 'donation' ? '#DBEAFE' : '#F3E8FF',
                          color: data.type === 'donation' ? '#1E40AF' : '#6B21A8',
                          fontSize: '13px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span role="img" aria-label={data.type}>{data.type === 'donation' ? '💝' : '💳'}</span>
                          {data.type === 'donation' ? (languageObj.donation || 'Donation') : (languageObj.payment || 'Payment')}
                        </div>

                        {/* Recurring Info */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ fontSize: '16px' }}><span role="img" aria-label="info">ℹ️</span></div>
                          <span style={{ fontSize: '14px', color: '#6B7280' }}>
                            {languageObj.recurringInfo || "Recurring schedule"}
                          </span>
                        </div>
                      </div>
                      <div style={{ fontSize: '14px', color: '#9CA3AF' }}>
                        {data.recurringEndDate
                          ? `Until ${new Date(data.recurringEndDate).toLocaleDateString()}`
                          : (languageObj.continueUntilCancel || "Continue until you cancel")
                        }
                      </div>
                    </div>

                    {/* Amount and Frequency - 2 columns */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '16px',
                      marginBottom: '16px'
                    }}>
                      {/* Amount per charge */}
                      <div style={{
                        border: '1px solid #E5E7EB',
                        borderRadius: '12px',
                        padding: '16px'
                      }}>
                        <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
                          {this.state.isEditing && (this.state.draft.hasOwnProperty('coverFee') ? this.state.draft.coverFee : this.state.originalData.coverFee)
                            ? (languageObj.donationAmount || "Donation amount")
                            : (languageObj.amountPerCharge || "Amount per charge")}
                        </div>
                        {!this.state.isEditing ? (
                          <div style={{ fontSize: '24px', fontWeight: '600' }}>
                            HK
                            <NumericFormat
                              decimalSeparator="."
                              displayType="text"
                              valueIsNumericString={true}
                              value={data.amount}
                              allowNegative={false}
                              thousandSeparator={true}
                              prefix={"$"}
                              fixedDecimalScale={true}
                              decimalScale={2}
                            />
                          </div>
                        ) : (
                          <>
                            <input
                              type="number"
                              style={{
                                width: '100%',
                                maxWidth: '250px',
                                padding: '8px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px',
                                fontSize: '18px',
                                fontWeight: '500'
                              }}
                              value={this.state.draft.hasOwnProperty('amount') ? this.state.draft.amount : String(this.state.originalData.amount)}
                              onChange={(e) => this.handleDraftChange('amount', e.target.value)}
                              placeholder="100.00"
                            />
                            {(this.state.draft.hasOwnProperty('coverFee') ? this.state.draft.coverFee : this.state.originalData.coverFee) &&
                             this.state.feeBreakdown && (
                              <div style={{ fontSize: '12px', color: '#059669', marginTop: '6px', fontWeight: '500' }}>
                                Total charged: HK${parseFloat(this.state.feeBreakdown.totalAmount || 0).toFixed(2)}
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {/* Frequency */}
                      <div style={{
                        border: '1px solid #E5E7EB',
                        borderRadius: '12px',
                        padding: '16px'
                      }}>
                        <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
                          {languageObj.frequency || "Frequency"}
                        </div>
                        {!this.state.isEditing ? (
                          <>
                            <div style={{ fontSize: '18px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span role="img" aria-label="calendar">🗓️</span>
                              <span>
                                {data.recurringInterval === 'weekly' && 'Weekly'}
                                {data.recurringInterval === 'monthly' && 'Monthly'}
                              </span>
                            </div>
                            {data.recurringInterval === 'monthly' && data.recurringDay && (
                              <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px' }}>
                                Day {data.recurringDay} of the month
                              </div>
                            )}
                            {data.recurringInterval === 'weekly' && data.scheduledDate && (
                              <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px' }}>
                                Every {new Date(data.scheduledDate).toLocaleDateString('en-US', { weekday: 'long' })}
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            {/* Frequency dropdown - always visible */}
                            <select
                              style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: '500',
                                boxSizing: 'border-box'
                              }}
                              value={this.state.draft.hasOwnProperty('recurringInterval') ? this.state.draft.recurringInterval : this.state.originalData.recurringInterval}
                              onChange={(e) => this.handleDraftChange('recurringInterval', e.target.value)}
                            >
                              <option value="weekly">Weekly</option>
                              <option value="monthly">Monthly</option>
                            </select>

                            {/* Day of month for Monthly */}
                            {(this.state.draft.hasOwnProperty('recurringInterval') ? this.state.draft.recurringInterval : this.state.originalData.recurringInterval) === 'monthly' && (
                              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '14px', color: '#6B7280' }}>Day of month</span>
                                <input
                                  type="number"
                                  min="1"
                                  max="31"
                                  style={{
                                    width: '80px',
                                    padding: '8px',
                                    border: '1px solid #E5E7EB',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    boxSizing: 'border-box'
                                  }}
                                  value={this.state.draft.hasOwnProperty('recurringDay') ? (this.state.draft.recurringDay || '') : (this.state.originalData.recurringDay || '')}
                                  onChange={(e) => {
                                    const newDay = e.target.value ? parseInt(e.target.value) : null;
                                    if (newDay && newDay >= 1 && newDay <= 31) {
                                      const currentScheduled = new Date(this.state.originalData.scheduledDate);
                                      const newDate = new Date(currentScheduled);
                                      newDate.setDate(newDay);
                                      // If the new day already passed this month, move to next month
                                      if (newDate <= new Date()) {
                                        newDate.setMonth(newDate.getMonth() + 1);
                                      }
                                      this.handleDraftChanges({
                                        recurringDay: newDay,
                                        scheduledDate: newDate.toISOString()
                                      });
                                    } else {
                                      this.handleDraftChange('recurringDay', newDay);
                                    }
                                  }}
                                  placeholder="1-31"
                                />
                              </div>
                            )}

                            {/* Day of week for Weekly */}
                            {(this.state.draft.hasOwnProperty('recurringInterval') ? this.state.draft.recurringInterval : this.state.originalData.recurringInterval) === 'weekly' && (
                              <div style={{ marginTop: '8px' }}>
                                <label style={{ display: 'block', fontSize: '14px', color: '#6B7280', marginBottom: '4px' }}>
                                  Day of week
                                </label>
                                <select
                                  style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #E5E7EB',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    boxSizing: 'border-box'
                                  }}
                                  value={(() => {
                                    const scheduledDate = this.state.draft.hasOwnProperty('scheduledDate')
                                      ? this.state.draft.scheduledDate
                                      : this.state.originalData.scheduledDate;
                                    return new Date(scheduledDate).getDay();
                                  })()}
                                  onChange={(e) => {
                                    // Calculate new scheduled date with selected day of week
                                    const currentDate = new Date(this.state.draft.hasOwnProperty('scheduledDate')
                                      ? this.state.draft.scheduledDate
                                      : this.state.originalData.scheduledDate);
                                    const currentDay = currentDate.getDay();
                                    const targetDay = parseInt(e.target.value);
                                    const dayDiff = targetDay - currentDay;

                                    const newDate = new Date(currentDate);
                                    newDate.setDate(newDate.getDate() + dayDiff);

                                    this.handleDraftChange('scheduledDate', newDate.toISOString());
                                  }}
                                >
                                  <option value="0">Sunday</option>
                                  <option value="1">Monday</option>
                                  <option value="2">Tuesday</option>
                                  <option value="3">Wednesday</option>
                                  <option value="4">Thursday</option>
                                  <option value="5">Friday</option>
                                  <option value="6">Saturday</option>
                                </select>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Duration / End Date */}
                    <div style={{
                      border: '1px solid #E5E7EB',
                      borderRadius: '12px',
                      padding: '16px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
                        {languageObj.duration || "Duration"}
                      </div>
                      {!this.state.isEditing ? (
                        <div style={{ fontSize: '18px', fontWeight: '500' }}>
                          {data.recurringEndDate
                            ? `Until ${new Date(data.recurringEndDate).toLocaleDateString()}`
                            : (languageObj.continueUntilCancel || "Continue until you cancel")
                          }
                        </div>
                      ) : (
                        <div>
                          {/* Toggle between infinite and limited duration */}
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={this.state.draft.hasOwnProperty('isInfiniteRecurring') ? this.state.draft.isInfiniteRecurring : this.state.originalData.isInfiniteRecurring}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    // Set to infinite (0 occurrences) - batch all updates
                                    this.handleDraftChanges({
                                      recurringOccurrences: 0,
                                      recurringEndDate: null,
                                      isInfiniteRecurring: true
                                    });
                                  } else {
                                    // Set to default (3 occurrences)
                                    // Use originalData values directly (they're not being modified, so they're correct)
                                    const interval = this.state.originalData.recurringInterval;
                                    const scheduledDate = this.state.originalData.scheduledDate;

                                    // Calculate end date with non-stale values
                                    const endDate = this.calculateEndDateFromOccurrences(3, scheduledDate, interval);

                                    // Update all fields - batch all updates
                                    this.handleDraftChanges({
                                      recurringOccurrences: 3,
                                      isInfiniteRecurring: false,
                                      recurringEndDate: endDate
                                    });
                                  }
                                }}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                              />
                              <span style={{ fontSize: '16px' }}>
                                {languageObj.continueUntilCancel || "Continue until you cancel"}
                              </span>
                            </label>
                          </div>

                          {/* Show occurrences input if not infinite */}
                          {!(this.state.draft.hasOwnProperty('isInfiniteRecurring') ? this.state.draft.isInfiniteRecurring : this.state.originalData.isInfiniteRecurring) && (
                            <div style={{ marginTop: '8px' }}>
                              <label style={{ display: 'block', fontSize: '14px', color: '#6B7280', marginBottom: '4px' }}>
                                {languageObj.numberOfTimes || "Number of times"}
                              </label>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                  type="number"
                                  min="1"
                                  max="999"
                                  style={{
                                    width: '100px',
                                    padding: '8px',
                                    border: '1px solid #E5E7EB',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    boxSizing: 'border-box'
                                  }}
                                  value={this.state.draft.hasOwnProperty('recurringOccurrences') ? this.state.draft.recurringOccurrences : this.state.originalData.recurringOccurrences}
                                  onChange={(e) => {
                                    const occurrences = parseInt(e.target.value) || 0;

                                    // Calculate end date using originalData to avoid stale state
                                    const interval = this.state.originalData.recurringInterval;
                                    const scheduledDate = this.state.originalData.scheduledDate;
                                    const endDate = this.calculateEndDateFromOccurrences(occurrences, scheduledDate, interval);

                                    // Batch both updates
                                    this.handleDraftChanges({
                                      recurringOccurrences: occurrences,
                                      recurringEndDate: endDate
                                    });
                                  }}
                                  placeholder="e.g., 12"
                                />
                                <span style={{ fontSize: '14px', color: '#6B7280' }}>
                                  times
                                  {(() => {
                                    const occurrences = this.state.draft.hasOwnProperty('recurringOccurrences')
                                      ? this.state.draft.recurringOccurrences
                                      : this.state.originalData.recurringOccurrences;
                                    const interval = this.state.draft.hasOwnProperty('recurringInterval')
                                      ? this.state.draft.recurringInterval
                                      : this.state.originalData.recurringInterval;
                                    const scheduledDate = this.state.draft.hasOwnProperty('scheduledDate')
                                      ? this.state.draft.scheduledDate
                                      : this.state.originalData.scheduledDate;
                                    const endDate = this.calculateEndDateFromOccurrences(occurrences, scheduledDate, interval);
                                    if (endDate) {
                                      return ` (until ${new Date(endDate).toLocaleDateString()})`;
                                    }
                                    return '';
                                  })()}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* 3 columns: Times Processed, Next Payment, Payment Method */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                      gap: '16px',
                      marginBottom: '16px'
                    }}>
                      {/* Times Processed */}
                      <div style={{
                        border: '1px solid #E5E7EB',
                        borderRadius: '12px',
                        padding: '16px'
                      }}>
                        <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
                          {languageObj.timesProcessed || "Times processed"}
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: '500' }}>
                          {data.recurringCount !== undefined ? data.recurringCount : 0} {data.recurringCount === 1 ? 'time' : 'times'}
                        </div>
                      </div>

                      {/* Next Payment */}
                      <div style={{
                        border: '1px solid #E5E7EB',
                        borderRadius: '12px',
                        padding: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
                            {languageObj.nextPayment || "Next payment"}
                          </div>
                          {(() => {
                            if (data.status === 'scheduled') {
                              const today = new Date();
                              const nextDate = new Date(data.scheduledDate);
                              const diffTime = nextDate - today;
                              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                              return (
                                <>
                                  <div style={{ fontSize: '18px', fontWeight: '500' }}>
                                    {date}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '2px' }}>
                                    {diffDays > 0 ? `in ${diffDays} ${diffDays === 1 ? 'day' : 'days'}` : 'today'} 
                                  </div>
                                </>
                              );
                            } else if (data.recurringCancelledAt) {
                              return (
                                <>
                                  <div style={{ fontSize: '18px', fontWeight: '500', color: '#DC2626' }}>
                                    Cancelled
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '2px' }}>
                                    {new Date(data.recurringCancelledAt).toLocaleDateString()}
                                  </div>
                                </>
                              );
                            } else {
                              return (
                                <div style={{ fontSize: '18px', fontWeight: '500' }}>
                                  {date}
                                </div>
                              );
                            }
                          })()}
                        </div>
                        <div style={{ fontSize: '20px' }}><span role="img" aria-label="calendar">🗓️</span></div>
                      </div>

                      {/* Payment Method */}
                      <div style={{
                        border: '1px solid #E5E7EB',
                        borderRadius: '12px',
                        padding: '16px'
                      }}>
                        <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>
                          {languageObj.paymentMethod || "Payment method"}
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: (this.state.isEditing && data.isRecurring) ? '8px' : '0' }}>
                          <span role="img" aria-label="credit card">💳</span>
                          <span>{data.cardBrand} •••• {data.cardLast4}</span>
                        </div>
                        {this.state.isEditing && data.isRecurring && (
                          <button
                            onClick={this.openCardModal}
                            style={{
                              fontSize: '12px',
                              color: '#667eea',
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '0',
                              textDecoration: 'underline'
                            }}
                          >
                            {languageObj.changeCard || "Change card"}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Total Contributed */}
                    {this.state.paymentHistory && this.state.paymentHistory.length > 0 && (
                      <div style={{
                        border: '1px solid #E5E7EB',
                        borderRadius: '12px',
                        padding: '16px',
                        background: '#F9FAFB'
                      }}>
                        <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '4px' }}>
                          {languageObj.totalContributed || "Total contributed"}
                        </div>
                        <div style={{ fontSize: '20px', fontWeight: '600' }}>
                          HK
                          <NumericFormat
                            decimalSeparator="."
                            displayType="text"
                            valueIsNumericString={true}
                            value={this.state.paymentHistory.reduce((sum, p) => sum + p.amount, 0)}
                            allowNegative={false}
                            thousandSeparator={true}
                            prefix={"$"}
                            fixedDecimalScale={true}
                            decimalScale={2}
                          />
                          <span style={{ fontSize: '14px', color: '#6B7280', marginLeft: '8px', fontWeight: 'normal' }}>
                            ({this.state.paymentHistory.length} {this.state.paymentHistory.length === 1 ? 'payment' : 'payments'})
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Initial Scheduled Date - small note at bottom */}
                    {(() => {
                      const firstPayment = this.state.paymentHistory && this.state.paymentHistory.length > 0
                        ? this.state.paymentHistory[0]
                        : data;
                      const firstDate = new Date(firstPayment.createdAt || firstPayment.scheduledDate);
                      const firstDateStr = `${firstDate.getFullYear()}/${firstDate.getMonth() + 1}/${firstDate.getDate()}`;

                      return (
                        <div style={{
                          fontSize: '12px',
                          color: '#9CA3AF',
                          textAlign: 'right',
                          marginTop: '8px'
                        }}>
                          {languageObj.initialScheduledDate || "Initial scheduled date"}: {firstDateStr}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Note Section */}
                  {(data.memo || this.state.isEditing) && (
                    <div style={{
                      border: '1px solid #E5E7EB',
                      borderRadius: '12px',
                      padding: '16px',
                      marginTop: '16px',
                      marginLeft: '20px',
                      marginRight: '20px'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        color: '#6B7280',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span role="img" aria-label="note">💬</span>
                        <span>{languageObj.note || languageObj.memo || "Note"}</span>
                      </div>
                      {!this.state.isEditing ? (
                        <div style={{
                          marginTop: '8px',
                          fontSize: '14px',
                          color: '#1F2937',
                          lineHeight: '1.5',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {data.memo || '—'}
                        </div>
                      ) : (
                        <textarea
                          style={{
                            marginTop: '8px',
                            width: '100%',
                            maxWidth: '600px',
                            padding: '8px',
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontFamily: 'inherit',
                            lineHeight: '1.5',
                            minHeight: '60px',
                            resize: 'vertical',
                            boxSizing: 'border-box'
                          }}
                          rows={3}
                          value={this.state.draft.hasOwnProperty('memo') ? this.state.draft.memo : this.state.originalData.memo}
                          onChange={(e) => this.handleDraftChange('memo', e.target.value)}
                          placeholder="Add a short note for this schedule (visible to the donor)."
                        />
                      )}
                    </div>
                  )}

                  {/* Cover Fee Section - only show if enabled or in edit mode */}
                  {(data.coverFee || this.state.isEditing) && (
                    <div style={{
                      border: '1px solid #E5E7EB',
                      borderRadius: '12px',
                      padding: '16px',
                      background: 'white',
                      marginTop: '16px',
                      marginLeft: '20px',
                      marginRight: '20px'
                    }}>
                      {!this.state.isEditing ? (
                        // View mode - show current status (only when enabled)
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          fontSize: '14px',
                          color: '#374151'
                        }}>
                          <span style={{
                            fontSize: '18px',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            ✅
                          </span>
                          <span>
                            <span role="img" aria-label="heart">💝</span> Covering transaction fees
                          </span>
                        </div>
                      ) : (
                        // Edit mode - show checkbox
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          color: '#374151'
                        }}>
                          <input
                            type="checkbox"
                            checked={this.state.draft.hasOwnProperty('coverFee') ? this.state.draft.coverFee : this.state.originalData.coverFee}
                            onChange={(e) => this.handleCoverFeeToggle(e.target.checked)}
                            disabled={this.state.isFeeLoading}
                            style={{
                              width: '18px',
                              height: '18px',
                              cursor: this.state.isFeeLoading ? 'wait' : 'pointer'
                            }}
                          />
                          <span>
                            <span role="img" aria-label="heart">💝</span> I want to cover the transaction fees so 100% of my donation goes to the cause
                            {this.state.isFeeLoading && <span style={{ marginLeft: '8px', color: '#6B7280' }}>(Calculating...)</span>}
                          </span>
                        </label>
                      )}

                      {/* Fee breakdown display when coverFee is enabled in edit mode */}
                      {this.state.isEditing &&
                       (this.state.draft.hasOwnProperty('coverFee') ? this.state.draft.coverFee : this.state.originalData.coverFee) &&
                       this.state.feeBreakdown && (
                        <div style={{
                          marginTop: '12px',
                          padding: '12px',
                          background: '#F0FDF4',
                          borderRadius: '8px',
                          fontSize: '13px',
                          color: '#166534'
                        }}>
                          <div style={{ marginBottom: '4px' }}>
                            Base amount: HK${parseFloat(this.state.feeBreakdown.nonprofitReceives || 0).toFixed(2)}
                          </div>
                          <div style={{ marginBottom: '4px' }}>
                            Transaction fee: HK${parseFloat(this.state.feeBreakdown.totalFee || 0).toFixed(2)}
                          </div>
                          <div style={{ fontWeight: '600' }}>
                            Total to be charged: HK${parseFloat(this.state.feeBreakdown.totalAmount || 0).toFixed(2)}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Payment History Section */}
                  {this.state.paymentHistory && this.state.paymentHistory.length > 0 && (
                    <div style={{
                      border: '1px solid #E5E7EB',
                      borderRadius: '12px',
                      padding: '20px',
                      background: 'white',
                      marginTop: '20px'
                    }}>
                      <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: '600' }}>
                        <span role="img" aria-label="history">📜</span> {languageObj.paymentHistory || "Payment History"}
                      </h3>

                      {this.state.loadingHistory ? (
                        <div style={{ textAlign: 'center', color: '#6B7280', padding: '20px' }}>
                          Loading history...
                        </div>
                      ) : (
                        <>
                          {/* Total contributed */}
                          <div style={{
                            border: '1px solid #E5E7EB',
                            borderRadius: '12px',
                            padding: '15px',
                            background: '#F9FAFB',
                            marginBottom: '15px'
                          }}>
                            <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '8px' }}>
                              <span role="img" aria-label="money">💰</span> {languageObj.totalContributed || "Total contributed"}
                            </div>
                            <div style={{ fontSize: '20px', fontWeight: '600' }}>
                              HK
                              <NumericFormat
                                decimalSeparator="."
                                displayType="text"
                                valueIsNumericString={true}
                                value={this.state.paymentHistory.reduce((sum, p) => sum + p.amount, 0)}
                                allowNegative={false}
                                thousandSeparator={true}
                                prefix={"$"}
                                fixedDecimalScale={true}
                                decimalScale={2}
                              />
                              <span style={{ fontSize: '14px', color: '#6B7280', marginLeft: '8px', fontWeight: 'normal' }}>
                                ({this.state.paymentHistory.length} {this.state.paymentHistory.length === 1 ? 'payment' : 'payments'})
                              </span>
                            </div>
                          </div>

                          {/* Payment list - sorted from newest to oldest */}
                          <div style={{ display: 'grid', gap: '12px' }}>
                            {[...this.state.paymentHistory]
                              .sort((a, b) => new Date(b.scheduledDate) - new Date(a.scheduledDate))
                              .map((payment, index) => {
                                const pDate = new Date(payment.scheduledDate);
                                const pDateStr = `${pDate.getFullYear()}/${pDate.getMonth() + 1}/${pDate.getDate()}`;
                                const paymentNumber = this.state.paymentHistory.length - index;

                                return (
                                  <div key={payment.id} style={{
                                    border: '1px solid #E5E7EB',
                                    borderRadius: '12px',
                                    padding: '15px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: '12px'
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                      <div style={{ fontSize: '20px' }}><span role="img" aria-label="success">✅</span></div>
                                      <div>
                                        <div style={{ fontWeight: '500', fontSize: '15px' }}>
                                          Payment #{paymentNumber}
                                        </div>
                                        <div style={{ fontSize: '14px', color: '#6B7280', marginTop: '4px' }}>
                                          {pDateStr} • HK
                                          <NumericFormat
                                            decimalSeparator="."
                                            displayType="text"
                                            valueIsNumericString={true}
                                            value={payment.amount}
                                            allowNegative={false}
                                            thousandSeparator={true}
                                            prefix={"$"}
                                            fixedDecimalScale={true}
                                            decimalScale={2}
                                          />
                                        </div>
                                        {payment.organizationReceiptId && (
                                          <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '2px' }}>
                                            Receipt: {payment.organizationReceiptId}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {payment.receiptUrl && (
                                      <a
                                        href={payment.receiptUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          padding: '10px 16px',
                                          border: '1px solid #E5E7EB',
                                          borderRadius: '10px',
                                          textDecoration: 'none',
                                          fontSize: '14px',
                                          fontWeight: '500',
                                          color: '#374151',
                                          background: 'white',
                                          whiteSpace: 'nowrap',
                                          transition: 'background 0.2s'
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.background = '#F9FAFB'}
                                        onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                                      >
                                        <span role="img" aria-label="receipt">📄</span> {languageObj.downloadReceipt || "Download Receipt"}
                                      </a>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="DonationInfoRecurring-chin" />
          </div>
        </div>

        {/* Card Update Modal */}
        {this.state.showCardModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={this.closeCardModal}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                maxWidth: '500px',
                width: '90%',
                maxHeight: '90vh',
                overflow: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                  <span role="img" aria-label="credit card">💳</span> {this.props.languageObj.updatePaymentMethod || "Update Payment Method"}
                </h2>
                <button
                  onClick={this.closeCardModal}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '0',
                    lineHeight: '1'
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '12px' }}>
                  {this.props.languageObj.enterNewCardDetails || "Enter your new card details"}
                </div>
                <div
                  style={{
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    padding: '12px'
                  }}
                >
                  <CardElement
                    options={{
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#374151',
                          '::placeholder': {
                            color: '#9CA3AF'
                          }
                        },
                        invalid: {
                          color: '#EF4444'
                        }
                      }
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={this.closeCardModal}
                  disabled={this.state.updatingCard}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    background: 'white',
                    cursor: this.state.updatingCard ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: this.state.updatingCard ? 0.5 : 1
                  }}
                >
                  {this.props.languageObj.cancelBtn || "Cancel"}
                </button>
                <button
                  onClick={this.handleUpdateCard}
                  disabled={this.state.updatingCard}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '8px',
                    background: '#000',
                    color: 'white',
                    cursor: this.state.updatingCard ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: this.state.updatingCard ? 0.7 : 1
                  }}
                >
                  {this.state.updatingCard
                    ? (this.props.languageObj.updating || "Updating...")
                    : (this.props.languageObj.updateCard || "Update Card")
                  }
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  render() {
    const data = this.state.data;
    if (!data)
      return (
        <div className="DonationInfoRecurring-emptyPage">
          <div className="DonationInfoRecurring-center">
            <div className="DonationInfoRecurring-btm-load" />
          </div>
        </div>
      );
    else return this.renderRecord(data);
  }
}

DonationInfoRecurring.propTypes = {
  languageObj: PropTypes.object,
  stripe: PropTypes.object,
  elements: PropTypes.object,
};

// Wrapper component to use Stripe hooks with class component
function DonationInfoRecurringWithStripe(props) {
  const stripe = useStripe();
  const elements = useElements();

  return <DonationInfoRecurring {...props} stripe={stripe} elements={elements} />;
}

export default DonationInfoRecurringWithStripe;
